import { useState, type DragEvent } from "react";
import { Link } from "react-router";
import {
  FolderClosed,
  FolderInput,
  FolderMinus,
  MoreVertical,
  Pencil,
  Trash2,
} from "lucide-react";
import { Trans, useLingui } from "@lingui/macro";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import { Button } from "@app/components/ui/button";
import { Input } from "@app/components/ui/input";
import { Label } from "@app/components/ui/label";
import { Textarea } from "@app/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@app/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@app/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@app/components/ui/dialog";
import DatasetStateBadge from "@app/components/DatasetStateBadge";
import type { DatasetState } from "@app/utils/reference-dataset";

interface Dataset {
  id: string;
  name: string;
  description?: string;
  file_count: number;
  example_count?: number;
  created_at: string;
  state?: DatasetState;
}

interface DatasetCardProps {
  dataset: Dataset;
  onDelete?: (datasetId: string) => Promise<void>;
  onEdit?: (
    datasetId: string,
    updates: { name: string; description: string },
  ) => Promise<void>;
  /** Opens « Ranger dans un classeur » for this dataset. */
  onMove?: () => void;
  /** Takes it out of its classeur; set only when it is in one. */
  onUnfile?: () => void;
  /** The classeur it is filed in, shown where the page mixes classeurs (search results). */
  folderName?: string;
  /** Makes the card draggable onto a classeur. */
  onDragStart?: (event: DragEvent<HTMLDivElement>) => void;
  onDragEnd?: () => void;
  /** This card is the one being dragged. */
  dragging?: boolean;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function DatasetCard({
  dataset,
  onDelete,
  onEdit,
  onMove,
  onUnfile,
  folderName,
  onDragStart,
  onDragEnd,
  dragging = false,
}: DatasetCardProps) {
  const { t } = useLingui();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editName, setEditName] = useState(dataset.name);
  const [editDescription, setEditDescription] = useState(
    dataset.description ?? "",
  );
  const [editError, setEditError] = useState<string | null>(null);

  const hasMenu = Boolean(onDelete || onEdit || onMove || onUnfile);

  const handleDelete = async () => {
    if (!onDelete) return;
    setIsDeleting(true);
    try {
      await onDelete(dataset.id);
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const openEditDialog = () => {
    setEditName(dataset.name);
    setEditDescription(dataset.description ?? "");
    setEditError(null);
    setShowEditDialog(true);
  };

  const handleEditSave = async () => {
    if (!onEdit) return;
    const trimmedName = editName.trim();
    if (!trimmedName) {
      setEditError(t`Le nom est requis`);
      return;
    }
    setIsSaving(true);
    setEditError(null);
    try {
      await onEdit(dataset.id, {
        name: trimmedName,
        description: editDescription.trim(),
      });
      setShowEditDialog(false);
    } catch (err) {
      setEditError(
        err instanceof Error ? err.message : t`Erreur lors de l'enregistrement`,
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Card
        className={`hover:shadow-lg hover:border-primary/50 transition-shadow h-full relative group${onDragStart ? " cursor-grab active:cursor-grabbing" : ""}${dragging ? " opacity-50" : ""}`}
        draggable={Boolean(onDragStart)}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
      >
        {hasMenu && (
          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => e.preventDefault()}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onEdit && (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      openEditDialog();
                    }}
                  >
                    <Pencil className="mr-2 h-4 w-4" />
                    <Trans>Modifier</Trans>
                  </DropdownMenuItem>
                )}
                {onMove && (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      onMove();
                    }}
                  >
                    <FolderInput className="mr-2 h-4 w-4" />
                    <Trans>Ranger dans un classeur</Trans>
                  </DropdownMenuItem>
                )}
                {onUnfile && (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      onUnfile();
                    }}
                  >
                    <FolderMinus className="mr-2 h-4 w-4" />
                    <Trans>Retirer du classeur</Trans>
                  </DropdownMenuItem>
                )}
                {(onEdit || onMove || onUnfile) && onDelete && (
                  <DropdownMenuSeparator />
                )}
                {onDelete && (
                  <DropdownMenuItem
                    className="text-red-600 focus:text-red-600 cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowDeleteDialog(true);
                    }}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    <Trans>Supprimer</Trans>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
        {/* Not draggable itself when the card is: the card, not its URL, is what moves. */}
        <Link
          to={`/datasets/${dataset.id}`}
          className="block h-full"
          draggable={onDragStart ? false : undefined}
        >
          <CardHeader>
            <CardTitle className="text-xl pr-8">{dataset.name}</CardTitle>
            <DatasetStateBadge state={dataset.state} className="mt-1 self-start" />
          </CardHeader>
          <CardContent>
            <p className="text-slate-600 mb-4 line-clamp-2">
              {dataset.description || t`Aucune description`}
            </p>
            <div className="text-sm text-slate-500 mb-2">
              {dataset.example_count ?? dataset.file_count} <Trans>exemples</Trans>
            </div>
            <div className="text-xs text-slate-400">
              <Trans>Créé le {formatDate(dataset.created_at)}</Trans>
            </div>
            {folderName && (
              <div className="mt-3 inline-flex max-w-full items-center gap-1.5 rounded-md bg-blue-50 px-2 py-0.5 text-xs text-primary">
                <FolderClosed className="h-3 w-3 shrink-0" />
                <span className="truncate">{folderName}</span>
              </div>
            )}
          </CardContent>
        </Link>
      </Card>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle><Trans>Supprimer le jeu de données</Trans></AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>Êtes-vous sûr de vouloir supprimer « {dataset.name} » ? Cette action est irréversible et supprimera tous les fichiers associés.</Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}><Trans>Annuler</Trans></AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {isDeleting ? <Trans>Suppression...</Trans> : <Trans>Supprimer</Trans>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle><Trans>Modifier le jeu de données</Trans></DialogTitle>
            <DialogDescription>
              <Trans>Mettez à jour le nom et la description du jeu de données.</Trans>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor={`dataset-name-${dataset.id}`}>
                <Trans>Nom</Trans>
              </Label>
              <Input
                id={`dataset-name-${dataset.id}`}
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder={t`Ex. : Masson`}
                disabled={isSaving}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`dataset-description-${dataset.id}`}>
                <Trans>Description</Trans>
              </Label>
              <Textarea
                id={`dataset-description-${dataset.id}`}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder={t`Description du jeu de données`}
                rows={4}
                disabled={isSaving}
              />
            </div>
            {editError && (
              <p className="text-sm text-red-600">{editError}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditDialog(false)}
              disabled={isSaving}
            >
              <Trans>Annuler</Trans>
            </Button>
            <Button onClick={handleEditSave} disabled={isSaving}>
              {isSaving ? <Trans>Enregistrement...</Trans> : <Trans>Enregistrer</Trans>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
